import {
  Card,
  ErrorBoundary,
  Loading,
  ROW_TYPE,
  useReadOnlyDataStreamer,
  Widgets,
  WIDGETS_TYPE
} from '@uma/react-commons/dist/index';
import React, { useState } from 'react';
import { FeatureFlagProps } from 'src/App';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { getFieldsToFetchActiveSessions } from 'src/utils/getFieldsToFetch';
import { URLS } from 'src/utils/urls';
import Loader from '../Loader';
import {
  ActiveSessionsError as ActiveSessionsAnalyticsError,
  ManageActiveSessionsHyperlinkClicked
} from './analytics';
import { ActiveSessionsError, LoadingWrapper, SessionsContent } from './styles';

/**
 * Checks if the provided sessionInfo contains at least one session object with a valid sessionId.
 *
 * @param sessionInfo - A JSON string representing an array of session objects,
 *                     or an array of session objects.
 * @returns {boolean} - true if no valid sessionId is found, false otherwise.
 *
 */
export const hasInvalidSessionId = (sessionInfo: string | null): boolean => {
  let sessions: { sessionId?: string }[] = [];
  if (typeof sessionInfo === 'string') {
    try {
      sessions = JSON.parse(sessionInfo);
    } catch {
      sessions = [];
    }
  } else if (Array.isArray(sessionInfo)) {
    sessions = sessionInfo;
  }

  return (
    !Array.isArray(sessions) || !sessions.some((session) => !!session.sessionId)
  );
};

const ActiveSessions: React.FC<FeatureFlagProps> = ({
  securityShowActiveSessions,
  isLoading: featureFlagLoading
}) => {
  const { translate, northboundAPIs, logger } = useDependencyManagerContext();
  const country = northboundAPIs.v1.localization.country;
  const language = northboundAPIs.v1.localization.language;

  const [hpIdSessionInfo, setHpIdSessionInfo] = useState<{
    sessionInfo: string | null;
  }>({ sessionInfo: null });
  const { error: hpIdError, loading: hpIdLoading } = useReadOnlyDataStreamer(
    setHpIdSessionInfo,
    getFieldsToFetchActiveSessions,
    securityShowActiveSessions
  );

  const text = securityShowActiveSessions
    ? `${translate(
        'security.newActiveSessionsDescription',
        `See which devices have been used recently to sign in using your HP account. If something doesn't look right, consider changing your password now to ensure that only you can access your account.`
      )}`
    : `${translate(
        'security.activeSessionsDescription',
        'View and manage which devices have been recently used to sign in to your HP account.'
      )}`;
  const title = {
    content: securityShowActiveSessions
      ? `${translate(
          'security.newActiveSessions',
          'Active sessions and recent logins'
        )}`
      : `${translate('security.activeSessions', 'Active sessions')}`,
    type: securityShowActiveSessions ? ROW_TYPE.NONE : ROW_TYPE.EXTERNAL,
    link: {
      url: URLS.HpIdSecurity,
      dataTestId: 'ActiveSessionsLink'
    },
    analyticsEvent: ManageActiveSessionsHyperlinkClicked
  };

  const ActiveSessionsWidget: React.FC<{ country: string; language: string }> =
    Widgets[WIDGETS_TYPE.SECURITY_ACTIVE_SESSIONS];

  if (featureFlagLoading)
    return <Loader data-testid="active-sessions-loader" />;

  const isLoaded = !featureFlagLoading && !hpIdLoading;
  const noSessionId = hasInvalidSessionId(hpIdSessionInfo.sessionInfo);

  if (isLoaded && securityShowActiveSessions && (noSessionId || hpIdError)) {
    const errorMessage = {
      hpIdError: hpIdError,
      noSessionId: noSessionId
    };
    logger.error('Active Sessions Error:', errorMessage);

    return (
      <Card
        title={title}
        rowsColorChangeEnabled={false}
        data-testid="active-sessions-error"
      >
        <ActiveSessionsError>
          {text}
          <ErrorBoundary
            analyticsEvent={ActiveSessionsAnalyticsError(
              JSON.stringify(errorMessage) || 'HP ID Active Sessions error'
            )}
            loading={false}
            onRetry={() => window.location.reload()}
          />
        </ActiveSessionsError>
      </Card>
    );
  }

  return (
    <Card
      title={title}
      rowsColorChangeEnabled={false}
      data-testid="active-sessions"
    >
      <SessionsContent data-testid="active-sessions-content">
        <span>{text}</span>
        {securityShowActiveSessions &&
          (hpIdLoading ? (
            <LoadingWrapper>
              <Loading data-testid="active-sessions-hp-id-loader" />
            </LoadingWrapper>
          ) : (
            <ActiveSessionsWidget
              country={country}
              language={language}
            />
          ))}
      </SessionsContent>
    </Card>
  );
};

export default ActiveSessions;
